import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import PostList from './PostList';
import PostEditor from './PostEditor';
import CategoryManager from './CategoryManager';

const AdminDashboard = ({ onLogout }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState('posts'); // 'posts', 'create', 'edit', 'categories', 'comments', 'subscribers'
  const [currentPost, setCurrentPost] = useState(null);
  const [stats, setStats] = useState({
    totalPosts: 0,
    totalCategories: 0,
    totalComments: 0,
    totalViews: 0,
    totalSubscriptions: 0
  });
  const [comments, setComments] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // Set up axios with auth token and get user info
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userInfo);
    }
    
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch posts from API
      const postsResponse = await axios.get('/api/posts');
      setPosts(postsResponse.data);
      
      // Fetch stats
      try {
        const statsResponse = await axios.get('/api/dashboard/stats');
        setStats(statsResponse.data);
      } catch (statsErr) {
        console.error('Stats fetch error:', statsErr);
        // Calculate stats from posts if API fails
        const postsData = postsResponse.data;
        const statsData = {
          totalPosts: postsData.length,
          totalCategories: new Set(postsData.map(post => post.category)).size,
          totalComments: postsData.reduce((sum, post) => sum + (post.comments || 0), 0),
          totalViews: postsData.reduce((sum, post) => sum + (post.views || 0), 0),
          totalSubscriptions: 0
        };
        setStats(statsData);
      }
      
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch dashboard data. Please try again.');
      setLoading(false);
      console.error('Dashboard fetch error:', err);
    }
  };

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/comments');
      setComments(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch comments. Please try again.');
      setLoading(false);
      console.error('Comments fetch error:', err);
    }
  };

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/subscriptions');
      setSubscribers(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch subscribers. Please try again.');
      setLoading(false);
      console.error('Subscribers fetch error:', err);
    }
  };

  const handleCreatePost = () => {
    setCurrentPost(null);
    setCurrentView('create');
  };

  const handleEditPost = (post) => {
    setCurrentPost(post);
    setCurrentView('edit');
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`/api/posts/${postId}`);
      
      // Remove post from local state
      setPosts(posts.filter(post => post._id !== postId));
      setStats(prev => ({
        ...prev,
        totalPosts: prev.totalPosts - 1
      }));
    } catch (err) {
      setError('Failed to delete post. Please try again.');
      console.error('Delete post error:', err);
    }
  };

  const handleSavePost = async (postData, isNew) => {
    try {
      let response;
      if (isNew) {
        response = await axios.post('/api/posts', postData);
        setPosts([response.data, ...posts]);
        setStats(prev => ({
          ...prev,
          totalPosts: prev.totalPosts + 1
        }));
      } else {
        response = await axios.put(`/api/posts/${postData._id}`, postData);
        setPosts(posts.map(post => 
          post._id === postData._id ? response.data : post
        ));
      }
      
      setCurrentView('posts');
    } catch (err) {
      setError('Failed to save post. Please try again.');
      console.error('Save post error:', err);
    }
  };

  const handleApproveComment = async (commentId, approved) => {
    try {
      await axios.put(`/api/comments/${commentId}`, { approved });
      
      // Update comment in local state
      setComments(comments.map(comment => 
        comment._id === commentId ? { ...comment, approved } : comment
      ));
    } catch (err) {
      setError('Failed to update comment. Please try again.');
      console.error('Comment update error:', err);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      await axios.delete(`/api/comments/${commentId}`);
      
      // Remove comment from local state
      setComments(comments.filter(comment => comment._id !== commentId));
      setStats(prev => ({
        ...prev,
        totalComments: prev.totalComments - 1
      }));
    } catch (err) {
      setError('Failed to delete comment. Please try again.');
      console.error('Comment delete error:', err);
    }
  };

  const handleDeleteSubscriber = async (subscriberId) => {
    if (!window.confirm('Are you sure you want to delete this subscriber?')) return;
    
    try {
      await axios.delete(`/api/subscriptions/${subscriberId}`);
      
      // Remove subscriber from local state
      setSubscribers(subscribers.filter(sub => sub._id !== subscriberId));
      setStats(prev => ({
        ...prev,
        totalSubscriptions: prev.totalSubscriptions - 1
      }));
    } catch (err) {
      setError('Failed to delete subscriber. Please try again.');
      console.error('Subscriber delete error:', err);
    }
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    
    // Fetch data based on view
    if (view === 'comments') {
      fetchComments();
    } else if (view === 'subscribers') {
      fetchSubscribers();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    
    // Remove auth header
    delete axios.defaults.headers.common['Authorization'];
    
    // Call onLogout if provided
    if (typeof onLogout === 'function') {
      onLogout();
    }
    
    // Navigate to home page
    navigate('/');
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="spinner"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <p className="text-red-700">{error}</p>
          <button 
            onClick={fetchDashboardData}
            className="mt-2 text-red-700 underline"
          >
            Try Again
          </button>
        </div>
      );
    }

    switch (currentView) {
      case 'posts':
        return (
          <PostList 
            posts={posts} 
            onEdit={handleEditPost} 
            onDelete={handleDeletePost} 
          />
        );
      case 'create':
      case 'edit':
        return (
          <PostEditor 
            post={currentPost} 
            onSave={handleSavePost} 
            onCancel={() => setCurrentView('posts')} 
          />
        );
      case 'categories':
        return (
          <CategoryManager 
            onClose={() => setCurrentView('posts')}
          />
        );
      case 'comments':
        return (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <h3 className="text-xl font-semibold p-4 border-b">Manage Comments</h3>
            {comments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comment</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {comments.map(comment => (
                      <tr key={comment._id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700">
                              {comment.name.charAt(0)}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{comment.name}</div>
                              <div className="text-sm text-gray-500">{comment.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">{comment.content}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {new Date(comment.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            comment.approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {comment.approved ? 'Approved' : 'Pending'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleApproveComment(comment._id, !comment.approved)}
                            className={`mr-2 ${
                              comment.approved ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'
                            }`}
                          >
                            {comment.approved ? 'Unapprove' : 'Approve'}
                          </button>
                          <button
                            onClick={() => handleDeleteComment(comment._id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                No comments found.
              </div>
            )}
          </div>
        );
      case 'subscribers':
        return (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <h3 className="text-xl font-semibold p-4 border-b">Manage Subscribers</h3>
            {subscribers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {subscribers.map(subscriber => (
                      <tr key={subscriber._id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{subscriber.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{subscriber.name || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {new Date(subscriber.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            subscriber.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {subscriber.active ? 'Active' : 'Unsubscribed'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleDeleteSubscriber(subscriber._id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                No subscribers found.
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Header />
      
      <main className="admin-dashboard py-12">
        <div className="container mx-auto px-4">
          <div className="admin-header mb-8">
            <div className="admin-header-container">
              <h1 className="admin-title">Admin Dashboard</h1>
              <div className="admin-user">
                <span className="admin-user-name">Welcome, {user?.username || 'Admin'}</span>
                <button 
                  onClick={handleLogout}
                  className="admin-logout"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>

          <div className="admin-stats mb-8">
            <div className="admin-stat-card bg-blue-50 border-t-4 border-blue-500">
              <div className="admin-stat-value text-blue-600">{stats.totalPosts}</div>
              <div className="admin-stat-label">Total Posts</div>
            </div>
            <div className="admin-stat-card bg-green-50 border-t-4 border-green-500">
              <div className="admin-stat-value text-green-600">{stats.totalCategories}</div>
              <div className="admin-stat-label">Categories</div>
            </div>
            <div className="admin-stat-card bg-purple-50 border-t-4 border-purple-500">
              <div className="admin-stat-value text-purple-600">{stats.totalComments}</div>
              <div className="admin-stat-label">Comments</div>
            </div>
            <div className="admin-stat-card bg-amber-50 border-t-4 border-amber-500">
              <div className="admin-stat-value text-amber-600">{stats.totalViews}</div>
              <div className="admin-stat-label">Total Views</div>
            </div>
            <div className="admin-stat-card bg-indigo-50 border-t-4 border-indigo-500">
              <div className="admin-stat-value text-indigo-600">{stats.totalSubscriptions}</div>
              <div className="admin-stat-label">Subscribers</div>
            </div>
          </div>

          <div className="admin-content">
            <div className="admin-content-header">
              <h2 className="admin-content-title">
                {currentView === 'posts' && 'Manage Posts'}
                {currentView === 'create' && 'Create New Post'}
                {currentView === 'edit' && 'Edit Post'}
                {currentView === 'categories' && 'Manage Categories'}
                {currentView === 'comments' && 'Manage Comments'}
                {currentView === 'subscribers' && 'Manage Subscribers'}
              </h2>
              <div className="flex space-x-2">
                {currentView === 'posts' && (
                  <>
                    <button 
                      onClick={handleCreatePost}
                      className="btn btn-primary"
                    >
                      <svg className="w-5 h-5 mr-1 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                      </svg>
                      New Post
                    </button>
                    <button 
                      onClick={() => handleViewChange('categories')}
                      className="btn btn-outline"
                    >
                      <svg className="w-5 h-5 mr-1 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                      </svg>
                      Categories
                    </button>
                    <button 
                      onClick={() => handleViewChange('comments')}
                      className="btn btn-outline"
                    >
                      <svg className="w-5 h-5 mr-1 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
                      </svg>
                      Comments
                    </button>
                    <button 
                      onClick={() => handleViewChange('subscribers')}
                      className="btn btn-outline"
                    >
                      <svg className="w-5 h-5 mr-1 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                      </svg>
                      Subscribers
                    </button>
                  </>
                )}
                {currentView !== 'posts' && (
                  <button 
                    onClick={() => setCurrentView('posts')}
                    className="btn btn-outline"
                  >
                    <svg className="w-5 h-5 mr-1 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                    </svg>
                    Back to Posts
                  </button>
                )}
              </div>
            </div>
            <div className="admin-content-body">
              {renderContent()}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </>
  );
};

export default AdminDashboard;
